import type { AnalysisState, FormatSpec, VisualizationSpec, VisualizationStyle } from '@aibi/contracts';
import { EmptyState, Input, Select, Tabs, TabsContent, TabsList, TabsTrigger } from '@aibi/ui';
import { SlidersHorizontal } from 'lucide-react';
import { useState, type CSSProperties } from 'react';
import { useServices } from '../../../app/services';
import { themePaletteOf } from '../../../visualization/viz-style';
import { automaticTitle } from '../../model/card-title';
import { useSelectedCard } from '../../state/use-selected-card';
import { useUiDispatch, useUiState, type PropertiesTab } from '../../state/ui-state';
import { useWorkspace, useWorkspaceActions } from '../../state/workspace-context';
import { FieldSlotList } from './FieldSlotList';
import { ConditionalFormatSection } from './ConditionalFormatSection';
import { PropertyField, PropertyGroup, Toggle } from './PropertyField';

const CARTESIAN = new Set(['bar', 'column', 'line', 'area']);
const PALETTE_SLOTS = 8;

type FormatPreset = 'model' | 'number' | 'compact' | 'currency' | 'percent';

const FONT_SIZE_LABELS: Readonly<Record<VisualizationStyle['fontSize'], string>> = {
  sm: 'Pequena',
  md: 'Media',
  lg: 'Grande',
  xl: 'Muy grande',
};

function presetOf(format: FormatSpec | undefined): FormatPreset {
  if (format === undefined) return 'model';
  if (format.type === 'number') return format.compact === true ? 'compact' : 'number';
  if (format.type === 'currency') return 'currency';
  if (format.type === 'percent') return 'percent';
  return 'model';
}

/**
 * Panel derecho: edita la visualizacion seleccionada. Cada cambio es un comando sobre el
 * AnalysisState (que se revalida y se vuelve a planificar con el Analysis Engine).
 */
export function PropertiesPanel() {
  const card = useSelectedCard();
  const { propertiesTab } = useUiState();
  const uiDispatch = useUiDispatch();

  return (
    <aside
      aria-label="Propiedades de la visualizacion"
      className="flex h-full w-80 shrink-0 flex-col border-l border-border bg-panel"
    >
      {card === null ? (
        <EmptyState
          icon={<SlidersHorizontal />}
          title="Ninguna visualizacion seleccionada"
          description="Haga clic en una visualizacion del lienzo para editar sus propiedades."
        />
      ) : (
        <Tabs
          value={propertiesTab}
          onValueChange={(value) => {
            uiDispatch({ type: 'set-properties-tab', tab: value as PropertiesTab });
          }}
          className="min-h-0 flex-1"
        >
          <div className="border-b border-border p-2">
            <TabsList className="w-full">
              <TabsTrigger value="data">Datos</TabsTrigger>
              <TabsTrigger value="format">Formato</TabsTrigger>
              <TabsTrigger value="interaction">Interaccion</TabsTrigger>
            </TabsList>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <TabsContent value="data">
              <DataTab card={card} />
            </TabsContent>
            <TabsContent value="format">
              <FormatTab card={card} />
            </TabsContent>
            <TabsContent value="interaction">
              <InteractionTab card={card} />
            </TabsContent>
          </div>
        </Tabs>
      )}
    </aside>
  );
}

function DataTab({ card }: { readonly card: AnalysisState }) {
  const { catalog } = useWorkspace();
  const { execute } = useWorkspaceActions();
  const { rendererRegistry } = useServices();
  const sort = card.sorting[0];
  const fields = [
    ...card.dimensions.map((d) => ({
      key: d.key,
      name: catalog.get(d.dimensionId)?.name ?? d.key,
    })),
    ...card.measures.map((m) => ({ key: m.key, name: catalog.get(m.measureId)?.name ?? m.key })),
  ];

  return (
    <>
      <PropertyGroup title="Visualizacion">
        <PropertyField label="Tipo">
          {(id) => (
            <Select
              id={id}
              value={card.visualization.type}
              onChange={(event) => {
                execute({
                  type: 'set-visualization-type',
                  cardId: card.id,
                  visualization: event.target.value as VisualizationSpec['type'],
                });
              }}
            >
              {rendererRegistry.list().map((definition) => (
                <option key={definition.type} value={definition.type}>
                  {definition.label}
                  {definition.available ? '' : ' (proximamente)'}
                </option>
              ))}
            </Select>
          )}
        </PropertyField>
      </PropertyGroup>
      <PropertyGroup title="Dimensiones">
        <FieldSlotList card={card} kind="dimension" />
      </PropertyGroup>
      <PropertyGroup title="Medidas">
        <FieldSlotList card={card} kind="measure" />
      </PropertyGroup>
      <PropertyGroup title="Orden">
        <div className="grid grid-cols-[1fr_auto] gap-1.5">
          <Select
            aria-label="Campo de orden"
            value={sort?.key ?? ''}
            onChange={(event) => {
              const key = event.target.value;
              const first = fields[0]?.key;
              if (key === '') {
                if (first !== undefined)
                  execute({ type: 'set-sort', cardId: card.id, key: first, direction: null });
                return;
              }
              execute({
                type: 'set-sort',
                cardId: card.id,
                key,
                direction: sort?.direction ?? 'desc',
              });
            }}
          >
            <option value="">Orden natural</option>
            {fields.map((field) => (
              <option key={field.key} value={field.key}>
                {field.name}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Direccion del orden"
            value={sort?.direction ?? 'desc'}
            disabled={sort === undefined}
            className="w-28"
            onChange={(event) => {
              if (sort) {
                execute({
                  type: 'set-sort',
                  cardId: card.id,
                  key: sort.key,
                  direction: event.target.value === 'asc' ? 'asc' : 'desc',
                });
              }
            }}
          >
            <option value="desc">Descendente</option>
            <option value="asc">Ascendente</option>
          </Select>
        </div>
      </PropertyGroup>
      <PropertyGroup title="Agrupacion">
        <Toggle
          label="Total general"
          checked={card.grouping.grandTotal}
          onChange={(checked) => {
            execute({ type: 'set-grouping', cardId: card.id, grandTotal: checked });
          }}
        />
        <Toggle
          label="Subtotales por la primera dimension"
          checked={card.grouping.subtotals}
          onChange={(checked) => {
            execute({ type: 'set-grouping', cardId: card.id, subtotals: checked });
          }}
        />
      </PropertyGroup>
    </>
  );
}

function FormatTab({ card }: { readonly card: AnalysisState }) {
  const { catalog, model } = useWorkspace();
  const { execute } = useWorkspaceActions();
  const viz = card.visualization;
  const currency = model.settings.currency ?? 'USD';
  const presetFormat = (preset: FormatPreset): FormatSpec | null => {
    switch (preset) {
      case 'model':
        return null;
      case 'number':
        return { type: 'number', decimals: 0, thousandsSeparator: true };
      case 'compact':
        return { type: 'number', compact: true };
      case 'currency':
        return { type: 'currency', currency, decimals: 0 };
      case 'percent':
        return { type: 'percent', decimals: 1 };
    }
  };

  return (
    <>
      <PropertyGroup title="General">
        <PropertyField label="Titulo">
          {(id) => (
            <Input
              id={id}
              key={`${card.id}:${card.layout.title ?? ''}`}
              defaultValue={card.layout.title ?? ''}
              placeholder={automaticTitle(card, catalog)}
              onBlur={(event) => {
                if (event.target.value !== (card.layout.title ?? '')) {
                  execute({ type: 'rename-card', cardId: card.id, title: event.target.value });
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.currentTarget.blur();
              }}
            />
          )}
        </PropertyField>
        <Toggle
          label="Mostrar tooltip"
          checked={viz.tooltip.show}
          onChange={(show) => {
            execute({
              type: 'update-visualization',
              cardId: card.id,
              patch: { tooltip: { show } },
            });
          }}
        />
      </PropertyGroup>
      {CARTESIAN.has(viz.type) && (
        <PropertyGroup title="Grafico">
          <Toggle
            label="Mostrar leyenda"
            checked={viz.legend.show}
            onChange={(show) => {
              execute({
                type: 'update-visualization',
                cardId: card.id,
                patch: { legend: { show } },
              });
            }}
          />
          <PropertyField label="Posicion de la leyenda">
            {(id) => (
              <Select
                id={id}
                value={viz.legend.position}
                onChange={(event) => {
                  execute({
                    type: 'update-visualization',
                    cardId: card.id,
                    patch: {
                      legend: {
                        position: event.target.value as VisualizationSpec['legend']['position'],
                      },
                    },
                  });
                }}
              >
                <option value="bottom">Abajo</option>
                <option value="top">Arriba</option>
                <option value="left">Izquierda</option>
                <option value="right">Derecha</option>
              </Select>
            )}
          </PropertyField>
          <Toggle
            label="Etiquetas de datos"
            checked={viz.labels.show}
            onChange={(show) => {
              execute({
                type: 'update-visualization',
                cardId: card.id,
                patch: { labels: { show } },
              });
            }}
          />
          <Toggle
            label="Eje de categorias"
            checked={(viz.type === 'bar' ? viz.axes.y?.show : viz.axes.x?.show) ?? true}
            onChange={(show) => {
              execute({
                type: 'update-visualization',
                cardId: card.id,
                patch: { axes: viz.type === 'bar' ? { y: { show } } : { x: { show } } },
              });
            }}
          />
          <Toggle
            label="Eje de valores"
            checked={(viz.type === 'bar' ? viz.axes.x?.show : viz.axes.y?.show) ?? true}
            onChange={(show) => {
              execute({
                type: 'update-visualization',
                cardId: card.id,
                patch: { axes: viz.type === 'bar' ? { x: { show } } : { y: { show } } },
              });
            }}
          />
          <PropertyField label="Apilado">
            {(id) => (
              <Select
                id={id}
                value={viz.grouping.stacking}
                onChange={(event) => {
                  execute({
                    type: 'update-visualization',
                    cardId: card.id,
                    patch: {
                      stacking: event.target.value as VisualizationSpec['grouping']['stacking'],
                    },
                  });
                }}
              >
                <option value="none">Sin apilar</option>
                <option value="stacked">Apilado</option>
                <option value="percent">Apilado al 100 %</option>
              </Select>
            )}
          </PropertyField>
        </PropertyGroup>
      )}
      {card.measures.length > 0 && (
        <PropertyGroup title="Formato de medidas">
          {card.measures.map((measure) => (
            <PropertyField
              key={measure.key}
              label={catalog.get(measure.measureId)?.name ?? measure.key}
            >
              {(id) => (
                <Select
                  id={id}
                  value={presetOf(viz.formatting.fields[measure.key])}
                  onChange={(event) => {
                    execute({
                      type: 'update-visualization',
                      cardId: card.id,
                      patch: {
                        fieldFormat: {
                          key: measure.key,
                          format: presetFormat(event.target.value as FormatPreset),
                        },
                      },
                    });
                  }}
                >
                  <option value="model">Formato del modelo</option>
                  <option value="number">Numero</option>
                  <option value="compact">Compacto (1,2 mil)</option>
                  <option value="currency">Moneda ({currency})</option>
                  <option value="percent">Porcentaje</option>
                </Select>
              )}
            </PropertyField>
          ))}
        </PropertyGroup>
      )}
      <StyleSection card={card} />
      <ConditionalFormatSection card={card} />
      <PropertyGroup title="Densidad">
        <PropertyField label="Espaciado de filas y relleno">
          {(id) => (
            <Select
              id={id}
              value={card.layout.density}
              onChange={(event) => {
                execute({
                  type: 'set-card-density',
                  cardId: card.id,
                  density: event.target.value === 'compact' ? 'compact' : 'comfortable',
                });
              }}
            >
              <option value="comfortable">Comoda</option>
              <option value="compact">Compacta</option>
            </Select>
          )}
        </PropertyField>
      </PropertyGroup>
    </>
  );
}

function StyleSection({ card }: { readonly card: AnalysisState }) {
  const { execute } = useWorkspaceActions();
  const style = card.visualization.style;
  const themePalette = themePaletteOf();
  const palette = Array.from({ length: PALETTE_SLOTS }, (_, index) => {
    return style.palette[index] ?? themePalette[index] ?? '#64748B';
  });
  const customPalette = style.palette.length > 0;

  return (
    <PropertyGroup title="Estilo">
      <PropertyField label="Tamano de fuente">
        {(id) => (
          <Select
            id={id}
            value={style.fontSize}
            onChange={(event) => {
              execute({
                type: 'update-visualization',
                cardId: card.id,
                patch: {
                  style: { fontSize: event.target.value as VisualizationStyle['fontSize'] },
                },
              });
            }}
          >
            {(Object.keys(FONT_SIZE_LABELS) as VisualizationStyle['fontSize'][]).map((size) => (
              <option key={size} value={size}>
                {FONT_SIZE_LABELS[size]}
              </option>
            ))}
          </Select>
        )}
      </PropertyField>
      <ColorField
        label="Color de texto"
        value={style.foreground ?? null}
        fallback="#0F172A"
        onChange={(foreground) => {
          execute({
            type: 'update-visualization',
            cardId: card.id,
            patch: { style: { foreground } },
          });
        }}
      />
      <ColorField
        label="Color de fondo"
        value={style.background ?? null}
        fallback="#FFFFFF"
        onChange={(background) => {
          execute({
            type: 'update-visualization',
            cardId: card.id,
            patch: { style: { background } },
          });
        }}
      />
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-medium text-muted-foreground">Paleta de series</span>
          {customPalette && (
            <button
              type="button"
              className="text-[11px] text-primary hover:underline"
              onClick={() => {
                execute({
                  type: 'update-visualization',
                  cardId: card.id,
                  patch: { style: { palette: [] } },
                });
              }}
            >
              Usar tema
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-0.5">
          {palette.map((color, index) => (
            <label
              key={`swatch-${String(index)}`}
              className="relative size-6 cursor-pointer overflow-hidden rounded-sm border border-border"
              title={`Serie ${String(index + 1)}`}
              style={{ backgroundColor: color } satisfies CSSProperties}
            >
              <input
                type="color"
                aria-label={`Color de serie ${String(index + 1)}`}
                value={color}
                className="absolute inset-0 cursor-pointer opacity-0"
                onChange={(event) => {
                  const next = [...palette];
                  next[index] = event.target.value.toUpperCase();
                  execute({
                    type: 'update-visualization',
                    cardId: card.id,
                    patch: { style: { palette: next } },
                  });
                }}
              />
            </label>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground">
          Barras, columnas, lineas y areas. Vacio = tema.
        </p>
      </div>
    </PropertyGroup>
  );
}

function ColorField({
  label,
  value,
  fallback,
  onChange,
}: {
  readonly label: string;
  readonly value: string | null;
  readonly fallback: string;
  readonly onChange: (value: string | null) => void;
}) {
  const current = value ?? fallback;
  const [draft, setDraft] = useState(value ?? '');
  const [synced, setSynced] = useState(value);
  if (value !== synced) {
    setSynced(value);
    setDraft(value ?? '');
  }

  const commitDraft = (raw: string) => {
    const next = raw.trim();
    if (next === '') {
      onChange(null);
      return;
    }
    if (/^#[0-9A-Fa-f]{6}$/.test(next)) onChange(next.toUpperCase());
    else setDraft(value ?? '');
  };

  return (
    <PropertyField label={label}>
      {(id) => (
        <div className="flex items-center gap-2">
          <label
            htmlFor={id}
            className="flex size-8 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-md border border-border"
            style={{ backgroundColor: current }}
            title={current}
          >
            <input
              id={id}
              type="color"
              value={current}
              className="size-10 cursor-pointer opacity-0"
              onChange={(event) => {
                onChange(event.target.value.toUpperCase());
              }}
            />
          </label>
          <Input
            aria-label={`${label} hexadecimal`}
            value={draft}
            placeholder="Tema"
            className="font-mono text-xs uppercase"
            maxLength={7}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
            onBlur={() => {
              commitDraft(draft);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                commitDraft(draft);
              }
            }}
          />
          {value !== null && (
            <button
              type="button"
              className="shrink-0 text-[11px] text-primary hover:underline"
              onClick={() => {
                onChange(null);
              }}
            >
              Restablecer
            </button>
          )}
        </div>
      )}
    </PropertyField>
  );
}

function InteractionTab({ card }: { readonly card: AnalysisState }) {
  const { execute } = useWorkspaceActions();
  return (
    <>
      <PropertyGroup title="Seleccion">
        <Toggle
          label="Permitir seleccionar valores"
          checked={card.visualization.interaction.selectable}
          onChange={(selectable) => {
            execute({
              type: 'update-visualization',
              cardId: card.id,
              patch: { interaction: { selectable } },
            });
          }}
        />
        <PropertyField label="Modo de seleccion">
          {(id) => (
            <Select
              id={id}
              value={card.interaction.selectionMode}
              onChange={(event) => {
                execute({
                  type: 'set-interaction',
                  cardId: card.id,
                  interaction: {
                    selectionMode: event.target.value === 'single' ? 'single' : 'multiple',
                  },
                });
              }}
            >
              <option value="multiple">Multiple (Ctrl+clic amplia)</option>
              <option value="single">Unica</option>
            </Select>
          )}
        </PropertyField>
        <Toggle
          label="Filtrado cruzado con otras visualizaciones"
          checked={card.interaction.crossFilter}
          onChange={(crossFilter) => {
            execute({ type: 'set-interaction', cardId: card.id, interaction: { crossFilter } });
          }}
        />
      </PropertyGroup>
      <PropertyGroup title="Drilldown">
        <Toggle
          label="Profundizar en jerarquias (doble clic)"
          checked={card.interaction.drillMode === 'hierarchy'}
          onChange={(enabled) => {
            execute({
              type: 'set-interaction',
              cardId: card.id,
              interaction: { drillMode: enabled ? 'hierarchy' : 'none' },
            });
          }}
        />
        <p className="text-xs text-muted-foreground">
          Disponible en dimensiones que pertenecen a una jerarquia del modelo (p. ej. Region, Pais,
          Ciudad).
        </p>
      </PropertyGroup>
    </>
  );
}
